import {
  DocVehicleQualification,
  DocumentData,
} from "@1games-ru/region-shared/dist/types/documents/documentData";
import React, { ReactNode, useCallback, useState } from "react";
import clsx from "clsx";
import { ArrowRightLeftIcon } from "@/shared/icons";

function Li({ children }: { children: ReactNode }) {
  return (
    <div className="w-[1.25rem] h-[1rem] text-13 text-[#7B003B] font-bold shrink-0 whitespace-nowrap">
      {children}
    </div>
  );
}

function Td({
  children,
  borderRight,
  borderBottom,
  className,
}: {
  children?: ReactNode;
  borderRight?: boolean;
  borderBottom?: boolean;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        "h-[2rem] text-13 flex items-center gap-2.5 border-dark-800 px-2.5",
        {
          "border-r": borderRight,
          "border-b": borderBottom,
        },
        className,
      )}
    >
      {children}
    </div>
  );
}

const categories = [
  DocVehicleQualification.Motorcycles,
  DocVehicleQualification.PassengerCars,
  DocVehicleQualification.MediumTrucks,
  DocVehicleQualification.HeavyTrucks,
  DocVehicleQualification.Mopeds,
];

const vehicleIcon: Record<DocVehicleQualification, ReactNode> = {
  [DocVehicleQualification.Motorcycles]: (
    <img src="./svg/vehicle-category/motorcycles.svg" alt="" className="w-[1.187rem]" />
  ),
  [DocVehicleQualification.LightMotorcycles]: (
    <img src="./svg/vehicle-category/light-motorcycles.svg" alt="" className="w-[1.3rem]" />
  ),
  [DocVehicleQualification.PassengerCars]: (
    <img src="./svg/vehicle-category/passenger-cars.svg" alt="" className="w-[1.25rem]" />
  ),
  [DocVehicleQualification.QuadricyclesAndTricycles]: (
    <img
      src="./svg/vehicle-category/quadricycles-and-tricycles.svg"
      alt=""
      className="w-[1.31rem]"
    />
  ),
  [DocVehicleQualification.HeavyTrucks]: (
    <img src="./svg/vehicle-category/heavy-trucks.svg" alt="" className="w-[1.97rem]" />
  ),
  [DocVehicleQualification.MediumTrucks]: (
    <img src="./svg/vehicle-category/medium-trucks.svg" alt="" className="w-[1.56rem]" />
  ),
  [DocVehicleQualification.PassengerBuses]: (
    <img src="./svg/vehicle-category/passenger-buses.svg" alt="" className="w-[2.125rem]" />
  ),
  [DocVehicleQualification.SmallBuses]: (
    <img src="./svg/vehicle-category/small-buses.svg" alt="" className="w-[1.56rem]" />
  ),
  [DocVehicleQualification.Mopeds]: (
    <img src="./svg/vehicle-category/mopeds.svg" alt="" className="w-[1.187rem]" />
  ),
};

function Category({ type }: { type: DocVehicleQualification }) {
  return (
    <div className="flex items-center gap-2.5 text-13 font-bold text-dark-800">
      <div className="w-[1.5rem]">{type}</div>
      {vehicleIcon[type]}
    </div>
  );
}

export default function DriverLicense({
  docNumber,
  issuedAt,
  owner,
  expiresAt,
  qualifications,
}: DocumentData) {
  const [isFlip, setIsFlip] = useState(false);

  const toggleFlip = useCallback(() => {
    setIsFlip((state) => !state);
  }, []);

  return (
    <div
      className={clsx(
        "relative h-[18.5rem] w-[30rem] [perspective:3000px] isolate",
        "text-14 text-dark-800 font-medium",
      )}
    >
      <button
        type="button"
        className={clsx(
          "absolute flex items-center gap-1 z-2 bg-dark-800/20 hover:bg-dark-800/40 rounded-[0.5rem]",
          "py-1 px-2 -bottom-[2rem] left-1/2 -translate-x-1/2 transition-all text-white",
        )}
        onClick={(e) => {
          e.stopPropagation();
          toggleFlip();
        }}
      >
        Перевернуть
        <ArrowRightLeftIcon size={16} />
      </button>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={clsx(
          "relative w-full h-full transition-transform duration-500 [transform-style:preserve-3d]",
          { "rotate-y-180": isFlip },
        )}
        onClick={(e) => {
          e.stopPropagation();
          toggleFlip();
        }}
      >
        <div
          className="absolute flex items-stretch gap-5 h-full w-full rounded-[1.25rem] p-[1.875rem] [backface-visibility:hidden] bg-[#F6E6F7]"
          style={{
            backgroundImage: "url(./svg/documents/driver-id-front.webp)",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
          }}
        >
          <div className="w-fit shrink-0 flex flex-col justify-center">
            <img
              src={owner.photo}
              alt=""
              className={clsx(
                "w-[6.25rem] h-[8.125rem] pointer-events-none rounded-[0.75rem]",
                "bg-gradient-to-b from-[#8DA9B3] to-[#A8C9D2]",
              )}
            />
          </div>
          <div className="grow">
            <div className="text-[#7B003B] text-14 font-medium">ВОДИТЕЛЬСКОЕ УДОСТОВЕРЕНИЕ</div>
            <div className="flex flex-col gap-2.5 mt-4">
              <div className="flex items-start gap-2.5">
                <Li>1.</Li>
                <div className="uppercase">{[owner.lastName, owner.firstName].join(" ")}</div>
              </div>
              <div className="flex items-start gap-2.5">
                <Li>2.</Li>
                <div className="uppercase">
                  <div>{owner.dateOfBirth}</div>
                  <div className="mt-1">РОССИЯ</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <div className="flex items-start gap-2.5">
                  <Li>3a)</Li>
                  <div className="uppercase">{issuedAt}</div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Li>3b)</Li>
                  <div className="uppercase">{expiresAt}</div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Li>3c)</Li>
                  <div className="uppercase">ГИБДД 7800</div>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Li>4.</Li>
                <div className="uppercase">{docNumber}</div>
              </div>
              <div className="flex items-start gap-2.5">
                <Li>5.</Li>
                <div className="uppercase">ЛЕНИНГРАДСКАЯ ОБЛ.</div>
              </div>
              <div className="flex items-start gap-2.5">
                <Li>6.</Li>
                <div className="flex items-center gap-0.5 uppercase">
                  {qualifications.map((item) => (
                    <div
                      key={item.code}
                      className="text-13 font-extrabold border-[3px] px-0.5 border-dark-800"
                    >
                      {item.code}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="absolute left-[1.875rem] bottom-[1.875rem] flex items-end gap-2.5">
            <Li>7.</Li>
            {owner?.signature && (
              <div
                className="z-1 h-[4rem] translate-y-4 w-fit pointer-events-none"
                style={{
                  backgroundImage: `url(${owner.signature})`,
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              />
            )}
          </div>
        </div>
        <div
          className="absolute h-full w-full rounded-[1.25rem] p-[1.875rem] [transform:rotateY(180deg)] [backface-visibility:hidden] bg-[#F6E6F7]"
          style={{
            backgroundImage: "url(./svg/documents/driver-id-back.webp)",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
          }}
        >
          <div className="grid grid-cols-3 border border-dark-800 rounded-[0.5rem] overflow-clip">
            {/* 1 */}
            <Td borderBottom borderRight>
              <div />
            </Td>
            <Td borderBottom borderRight className="justify-center">
              <Li>8.</Li>
            </Td>
            <Td borderBottom className="justify-center">
              <Li>9.</Li>
            </Td>
            {/* 2 */}
            {categories.map((item, index) => {
              const findedCategory = qualifications.find(
                (qualification) => qualification.code === item,
              );

              const hasBottomBorder = index !== categories.length - 1;

              return (
                <React.Fragment key={item}>
                  <Td borderBottom={hasBottomBorder} borderRight>
                    <Category type={item} />
                  </Td>
                  <Td borderBottom={hasBottomBorder} borderRight className="justify-center">
                    {findedCategory ? findedCategory?.issuedAt || "" : ""}
                  </Td>
                  <Td borderBottom={hasBottomBorder} className="justify-center">
                    {findedCategory ? findedCategory?.expiresAt || "" : ""}
                  </Td>
                </React.Fragment>
              );
            })}
          </div>
          <div className="flex items-start gap-2.5 mt-2.5">
            <Li>10.</Li>
            <div className="uppercase">{issuedAt.split(".").slice(-1)[0]}</div>
          </div>
          <div className="absolute text-[#7B003B] text-15 font-medium right-[1.875rem] bottom-[1.875rem]">
            {docNumber}
          </div>
        </div>
      </div>
    </div>
  );
}
